import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, Clock, CreditCard, QrCode, Search, Package, ArrowLeft } from "lucide-react";
import LandingFooter from "@/components/landing/LandingFooter";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PedidoDetalhes {
  id: string;
  numero_pedido: string;
  total: number;
  status: string;
  metodo_pagamento: string;
  items: Array<{
    nome_produto: string;
    descricao_produto?: string;
    quantidade: number;
    preco_unitario: number;
    subtotal: number;
  }>;
  created_at: string;
  pix_qr_code?: string;
  pix_copy_paste?: string;
}

export default function AcompanharPedido() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [pedido, setPedido] = useState<PedidoDetalhes | null>(null);
  const [loading, setLoading] = useState(false);
  const [numeroPedido, setNumeroPedido] = useState(searchParams.get('pedido') || '');

  useEffect(() => {
    const pedidoParam = searchParams.get('pedido');
    if (pedidoParam) {
      setNumeroPedido(pedidoParam);
      buscarPedido(pedidoParam);
    }
  }, [searchParams]);

  // Helper function to check if input is a valid UUID format
  const isUUID = (str: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  const buscarPedido = async (pedidoId: string) => {
    if (!pedidoId.trim()) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Por favor, informe o número do pedido."
      });
      return;
    }

    setLoading(true);

    try {
      let pedidoData = null;

      // Check if input is a tracking token (32 hex characters) or secure token
      if (pedidoId.length === 32 && /^[0-9a-f]+$/i.test(pedidoId)) {
        // Use secure tracking token function
        const { data, error } = await supabase.rpc('get_secure_order_info', {
          p_tracking_token: pedidoId
        });
        
        if (error) {
          console.error('Erro ao buscar pedido por token:', error);
        } else if (data && data.length > 0) {
          const orderData = data[0];
          pedidoData = {
            numero_pedido: orderData.numero_pedido,
            total: orderData.total,
            status: orderData.status,
            metodo_pagamento: orderData.metodo_pagamento || 'cartao_credito',
            items: orderData.items || [],
            created_at: orderData.created_at,
            pix_qr_code: orderData.pix_qr_code,
            pix_copy_paste: orderData.pix_copy_paste
          };
        }
      } else {
        // Use public order info function (limited data)
        const { data, error } = await supabase.rpc('get_public_order_info', {
          p_numero_pedido: pedidoId
        });
        
        if (error) {
          console.error('Erro ao buscar pedido:', error);
        } else if (data && data.length > 0) {
          const orderData = data[0];
          pedidoData = {
            numero_pedido: orderData.numero_pedido,
            total: orderData.total,
            status: orderData.status,
            metodo_pagamento: orderData.metodo_pagamento || 'cartao_credito',
            items: orderData.items || [],
            created_at: orderData.created_at,
            tracking_token: orderData.tracking_token
          };
        }
      }

      if (!pedidoData) {
        toast({
          variant: "destructive",
          title: "Pedido não encontrado",
          description: "Verifique o número do pedido e tente novamente."
        });
        setPedido(null);
        return;
      }

      setPedido({
        id: pedidoData.tracking_token || pedidoId, // Use tracking token as ID for URL
        numero_pedido: pedidoData.numero_pedido,
        total: pedidoData.total,
        status: pedidoData.status,
        metodo_pagamento: pedidoData.metodo_pagamento,
        items: pedidoData.items,
        created_at: pedidoData.created_at,
        pix_qr_code: pedidoData.pix_qr_code,
        pix_copy_paste: pedidoData.pix_copy_paste
      });

      // Update URL with tracking token for secure access
      if (pedidoData.tracking_token && pedidoId !== pedidoData.tracking_token) {
        setSearchParams({ pedido: pedidoData.tracking_token });
      }
    } catch (error) {
      console.error('Erro ao buscar pedido:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Erro interno ao carregar pedido."
      });
      setPedido(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (numeroPedido.trim()) {
      setSearchParams({ pedido: numeroPedido.trim() });
    }
  };

  const getStatusIcon = () => {
    const currentStatus = pedido?.status;
    
    if (currentStatus === 'pagamento_aprovado' || currentStatus === 'pago' || currentStatus === 'aprovado') {
      return <CheckCircle className="h-16 w-16 text-emerald-400" />;
    } else if (currentStatus === 'pendente' || currentStatus === 'cobranca_criada') {
      return <Clock className="h-16 w-16 text-yellow-400" />;
    } else {
      return <Clock className="h-16 w-16 text-gray-400" />;
    }
  };

  const getStatusMessage = () => {
    const currentStatus = pedido?.status;
    
    if (currentStatus === 'pagamento_aprovado' || currentStatus === 'pago' || currentStatus === 'aprovado') {
      return {
        title: "Pagamento Aprovado! 🎉",
        description: "Seu pedido foi confirmado e processado com sucesso."
      };
    } else if (currentStatus === 'pendente' || currentStatus === 'cobranca_criada') {
      if (pedido?.metodo_pagamento === 'pix') {
        return {
          title: "Aguardando Pagamento PIX",
          description: "Realize o pagamento para confirmar seu pedido."
        };
      }
      return {
        title: "Pagamento em Processamento",
        description: "Estamos processando seu pagamento."
      };
    } else {
      return {
        title: "Status do Pagamento",
        description: "Acompanhe o status do seu pedido."
      };
    }
  };

  const getMethodIcon = () => {
    return pedido?.metodo_pagamento === 'pix' 
      ? <QrCode className="h-5 w-5" />
      : <CreditCard className="h-5 w-5" />;
  };

  const getMethodLabel = () => {
    return pedido?.metodo_pagamento === 'pix' 
      ? 'PIX'
      : 'Cartão de Crédito';
  };

  const copyPixCode = () => {
    if (pedido?.pix_copy_paste) {
      navigator.clipboard.writeText(pedido.pix_copy_paste);
      toast({
        title: "Código copiado!",
        description: "Código PIX copiado para a área de transferência."
      });
    }
  };

  const statusMessage = getStatusMessage();
  const currentStatus = pedido?.status;
  const isPagoOrAprovado = currentStatus === 'pagamento_aprovado' || currentStatus === 'pago' || currentStatus === 'aprovado';

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-bg-primary via-dark-bg-secondary to-dark-bg-tertiary dark relative overflow-hidden">
      {/* Floating background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-dark-accent/10 rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Package className="w-8 h-8 text-dark-accent" />
            <h1 className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent">
              Acompanhar Pedido
            </h1>
          </div>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Digite o número do seu pedido para acompanhar o status
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Search Section */}
          <div className="dark-card rounded-2xl border border-dark-border backdrop-blur-sm p-6 mb-8">
            <div className="flex gap-3">
              <Input
                type="text"
                placeholder="Digite o número do pedido..."
                value={numeroPedido}
                onChange={(e) => setNumeroPedido(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-gray-400"
              />
              <Button 
                onClick={handleSearch} 
                disabled={loading}
                className="bg-gradient-to-r from-dark-accent to-dark-accent-glow hover:from-dark-accent-glow hover:to-dark-accent"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {loading && (
            <div className="dark-card rounded-2xl border border-dark-border backdrop-blur-sm p-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dark-accent mx-auto mb-4"></div>
                <p className="text-gray-300">Buscando pedido...</p>
              </div>
            </div>
          )}

          {pedido && !loading && (
            <>
              {/* Status do Pagamento */}
              <div className="text-center mb-8">
                <div className="mb-6">
                  {getStatusIcon()}
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">
                  {statusMessage.title}
                </h2>
                <p className="text-gray-300 text-lg">
                  {statusMessage.description}
                </p>
              </div>

              {/* Detalhes do Pedido */}
              <div className="dark-card rounded-2xl border border-dark-border backdrop-blur-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-white">Detalhes do Pedido</h3>
                  <Badge 
                    variant={isPagoOrAprovado ? 'default' : 'secondary'}
                    className={isPagoOrAprovado ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : ''}
                  >
                    {isPagoOrAprovado ? 'Confirmado' : 'Processando'}
                  </Badge>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Número do Pedido:</span>
                    <span className="font-medium text-white">{pedido.numero_pedido}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Método de Pagamento:</span>
                    <div className="flex items-center gap-2 text-white">
                      {getMethodIcon()}
                      <span className="font-medium">{getMethodLabel()}</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Total:</span>
                    <span className="font-semibold text-xl text-white">
                      R$ {pedido.total.toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                </div>

                <Separator className="my-6 bg-dark-border" />

                {/* Lista de Produtos */}
                <div className="space-y-4">
                  <h4 className="font-medium text-white">Produtos Adquiridos:</h4>
                  {pedido.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-start p-4 bg-white/5 rounded-lg border border-white/10">
                      <div className="flex-1">
                        <h5 className="font-medium text-white">{item.nome_produto}</h5>
                        {item.descricao_produto && (
                          <p className="text-sm text-gray-400 mt-1">
                            {item.descricao_produto}
                          </p>
                        )}
                        <p className="text-sm text-gray-400 mt-1">
                          Quantidade: {item.quantidade}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-white">
                          R$ {item.subtotal.toFixed(2).replace('.', ',')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Informação PIX */}
              {pedido.metodo_pagamento === 'pix' && !isPagoOrAprovado && (
                <div className="dark-card rounded-2xl border border-dark-border backdrop-blur-sm p-6 mb-6">
                  <div className="text-center">
                    <QrCode className="h-12 w-12 text-dark-accent mx-auto mb-4" />
                    <h4 className="text-lg font-semibold text-white mb-3">Pagamento PIX Pendente</h4>
                    <p className="text-gray-300 mb-6">
                      Realize o pagamento via PIX para confirmar seu pedido. O processo é instantâneo!
                    </p>
                    
                    {pedido.pix_copy_paste && (
                      <div className="space-y-3">
                        <p className="text-sm text-gray-400">Código PIX Copia e Cola:</p>
                        <div className="flex gap-2">
                          <Input
                            value={pedido.pix_copy_paste}
                            readOnly
                            className="flex-1 bg-white/5 border-white/10 text-white font-mono text-xs"
                          />
                          <Button
                            onClick={copyPixCode}
                            variant="outline"
                            size="sm"
                            className="border-dark-accent text-dark-accent hover:bg-dark-accent hover:text-dark-bg-primary"
                          >
                            Copiar
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {!pedido && !loading && numeroPedido && (
            <div className="dark-card rounded-2xl border border-dark-border backdrop-blur-sm p-8 text-center">
              <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Pedido não encontrado</h3>
              <p className="text-gray-300 mb-6">
                Verifique se o número do pedido está correto e tente novamente.
              </p>
              <Button 
                onClick={() => setNumeroPedido('')}
                variant="outline"
                className="border-dark-accent text-dark-accent hover:bg-dark-accent hover:text-dark-bg-primary"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Nova Busca
              </Button>
            </div>
          )}
        </div>
      </div>
      
      <LandingFooter />
    </div>
  );
}